import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect, useState } from "react";

/**
 * Base package name of the root ("smcrm" / "Transform") app. Used as fallback
 * when the runtime package name cannot be resolved (e.g. web builds, which
 * ship without a native shell).
 */
export const DEFAULT_APP_PACKAGE = "com.smcrm.demo";

export interface AppPackages {
  iosPackage: string;
  androidPackage: string;
}

const STORED_PACKAGE_KEY = "smcrm_app_package_id";

let cachedPackageName: string | null = (() => {
  try {
    return localStorage.getItem(STORED_PACKAGE_KEY);
  } catch (e) {
    return null;
  }
})();

/**
 * Resolves the package name the app is currently running under.
 *
 * The app ships as multiple variants (see android/app/build.gradle and the iOS
 * App / AppTime targets in ios/App/App.xcodeproj):
 *   - smcrm : com.smcrm.demo      ("Transform") — the root app
 *   - time  : com.smcrmdemo.time  ("Time")      — the sub-variant app
 *
 * Both platforms expose the variant's real applicationId / bundle identifier
 * through @capacitor/app's App.getInfo().id, so no per-variant build flag is
 * needed — the value is simply read from the running app. The result is cached
 * because it can never change within a session.
 */
export const getAppPackageName = async (): Promise<string> => {
  if (cachedPackageName !== null && cachedPackageName !== DEFAULT_APP_PACKAGE) {
    return cachedPackageName;
  }

  let packageName = cachedPackageName || DEFAULT_APP_PACKAGE;

  if (Capacitor.isNativePlatform()) {
    try {
      const info = await App.getInfo();
      if (info?.id) {
        packageName = info.id;
        try {
          localStorage.setItem(STORED_PACKAGE_KEY, packageName);
        } catch (e) {}
      }
    } catch (err) {
      console.warn("[appPackage] Failed to read the app package name:", err);
    }
  }

  cachedPackageName = packageName;
  console.log("packageName:", packageName);
  return packageName;
};

// Eagerly resolve package name as early as possible
if (Capacitor.isNativePlatform()) {
  getAppPackageName();
}

/**
 * Convenience wrapper for the OTA updater (capacitor-3rddigital-appupdate):
 * returns the running app's package name for both platforms, matching the
 * updater hook's { iosPackage, androidPackage } option shape.
 */
export const getAppPackages = async (): Promise<AppPackages> => {
  const packageName = await getAppPackageName();

  return {
    iosPackage: packageName,
    androidPackage: packageName,
  };
};

/**
 * React glue for the async package-name resolution. Returns null until the
 * package name has been resolved, so consumers can defer work (e.g. the OTA
 * update check) until the correct per-variant package is known.
 */
export const useAppPackages = (): AppPackages | null => {
  const [appPackages, setAppPackages] = useState<AppPackages | null>(() => {
    if (cachedPackageName) {
      return {
        iosPackage: cachedPackageName,
        androidPackage: cachedPackageName,
      };
    }
    return null;
  });

  useEffect(() => {
    let isMounted = true;

    getAppPackages()
      .then((packages) => {
        if (packages?.iosPackage) {
          try {
            localStorage.setItem(STORED_PACKAGE_KEY, packages.iosPackage);
          } catch (e) {}
        }
        if (isMounted) {
          setAppPackages(packages);
        }
      })
      .catch((err) => {
        console.warn("[appPackage] Failed to resolve app packages:", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return appPackages;
};

// ---------------------------------------------------------------------------
// App variant configuration ("Time" variant)
// ---------------------------------------------------------------------------

/**
 * Package id of the "Time" variant app (Android flavor `time` / iOS AppTime
 * target). The root "Transform" app uses DEFAULT_APP_PACKAGE.
 */
export const TIME_VARIANT_PACKAGE = "com.smcrmdemo.time";

/**
 * Roles allowed to log in on the "Time" variant app. Any other role is
 * blocked from logging in there (frontend-side gate, see Auth/Redux/auth.ts).
 * Keys must match the `key` field of the Roles collection
 * ("attendance-tablet" === ROLE.ATTENDANCE_TABLET in helpers/utils.ts).
 */
export const TIME_VARIANT_ALLOWED_ROLES: string[] = [
  "employee",
  "attendance-tablet",
];

/** Message shown when a non-allowed role tries to log in to the Time variant. */
export const TIME_VARIANT_LOGIN_BLOCKED_MESSAGE =
  "This app is only for Employee and Attendance Tablet accounts. Please use the Transform app to log in.";

/** Message shown when password reset is attempted inside the Time variant. */
export const TIME_VARIANT_RESET_BLOCKED_MESSAGE =
  "Password reset is not available in this app. Please use the Transform app to reset your password.";

/** True when the given package id belongs to the "Time" variant app. */
export const isTimeVariantPackage = (packageName?: string | null): boolean =>
  packageName === TIME_VARIANT_PACKAGE;

/** True when the currently running app is the "Time" variant. */
export const isTimeVariant = async (): Promise<boolean> =>
  isTimeVariantPackage(await getAppPackageName());

/**
 * React counterpart of isTimeVariant(): false until the package name has been
 * resolved, then stays in sync. Use it for purely cosmetic gating (e.g.
 * replacing screens); always pair it with the async isTimeVariant() check
 * inside submit handlers so the gate cannot be raced.
 */
export const useIsTimeVariant = (): boolean => {
  const appPackages = useAppPackages();
  if (appPackages?.iosPackage) {
    return isTimeVariantPackage(appPackages.iosPackage);
  }
  if (cachedPackageName) {
    return isTimeVariantPackage(cachedPackageName);
  }
  return false;
};
